import CONSTANTS from '../utils/constants';
import AWS from 'aws-sdk';
import { ensureAuthenticated } from '../utils/auth/authenticate';
import CustomDataTableRecord from 'src/models/custom-data-tables/CustomDataTableRecord';
import { CustomCOARow } from 'src/models/custom-coa/CustomCOADefinition';
import ErrorUtils from 'src/utils/ErrorUtils';
import CreateMultiAllocationWorksheetRequest from "src/models/multidimensional-allocation/CreateMultiAllocationWorksheetRequest";
import { CreateMultiAllocationSheetRequest } from "src/models/multidimensional-allocation/CreateMultiAllocationSheetRequest";
import { UpdateMultiAllocationSheetRequest } from "src/models/multidimensional-allocation/UpdateMultiAllocationSheetRequest";
import { CreateCommonBalancePoolRequest, UpdateCommonBalancePoolRequest } from "src/models/multidimensional-allocation/CommonBalancePool";
import { EntityLockedError } from "src/models/common/EntityLockedError";
import CustomTag from "src/models/accounting-calendar/CustomTag";
import {
    CreateSegmentDataTableRequest,
    ListSegmentDataTablesRequest,
    SearchSegmentDataTableRowsRequest,
    SegmentRow,
} from "src/models/segment-cdt/SegmentCdtTypes";

let baseUrl = '/api';
let reportBaseUrl = '/report-api';
let orchestratorBaseUrl = '/orchestrator-api';
const environment = process.env.NODE_ENV;

// Common abstraction for calling any API. The parameters used for querying
// the API are all common among the API endpoints, so this method abstracts
// this functionality to make the API methods more readable.
async function _callExternalApi(method: string, url: string, body = null, headers = {}) {
    const auth = await ensureAuthenticated() as any;
    const session = auth.getSignInUserSession();
    const jwtToken = session.getIdToken().getJwtToken();

    // Utility function to check response status and handle errors based on status code
    async function checkStatus(response: any) {
        if (response.status >= 200 && response.status < 300) {
            // If everything looks good
            return response;
        }
        if (response.status === 417) {
            // Lambda API will return status code 417 with a message intended for the final user
            const responseBody = await response.json();
            const error = new Error(responseBody.message);
            error.message = responseBody.message;
            throw error;
        } else if (response.status === 422) {
            // Validation warning - suppressible by user
            const responseBody = await response.json();
            const error: any = new Error(responseBody.message);
            error.message = responseBody.message;
            error.isValidationWarning = true;
            throw error;
        } else if (response.status === 409) {
            const responseBody = await response.json().catch(() => ({} as any));
            throw new EntityLockedError(
                responseBody.message || 'This entity is locked by another user.',
                responseBody.lockedBy,
                responseBody.lockedAt,
            );
        } else if (response.status >= 300 && response.status < 500) {
            // If there are issues where the user could take action (not server side errors)
            const error = new Error(response.statusText);
            error.message = "There was a problem with the request. Error status code: "+response.status;
            throw error;
        } else {
            // All other status codes (mostly 500s will display this generic message)
            console.error(await response.json())
            const error = new Error(CONSTANTS.GENERIC_ERROR_MESSAGE);
            error.message = CONSTANTS.GENERIC_ERROR_MESSAGE;
            throw error;
        }
    }

    const response = await fetch(url, {
        method,
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            ...headers,
            Authorization: jwtToken,
        },
        redirect: 'follow',
        referrer: 'no-referrer',
        body: method === 'GET' ? null : JSON.stringify(body),
    });

    await checkStatus(response);

    return response;
}

async function _callBusinessApi(method: string, endpoint: string, body: any | undefined = null, headers = {}) {
    if (environment === CONSTANTS.PRODUCTION) {
        baseUrl = CONSTANTS.ENVIRONMENT_VARIABLES.BUSINESS_LOGIC_API as string;
    }

    const url = `${baseUrl}${endpoint}`;

    const result = await _callExternalApi(method, url, body, headers);

    return result;
}

async function _callReportingApi(method: string, endpoint: string, body: any | undefined = null, headers = {}) {
    if (environment === CONSTANTS.PRODUCTION) {
        reportBaseUrl = CONSTANTS.ENVIRONMENT_VARIABLES.REPORTS_API as string;
    }
    const url = `${reportBaseUrl}${endpoint}`;

    const result = await _callExternalApi(method, url, body, headers);

    return result;
}

// Calls the ICTPReportingOrchestrator API (the event-driven TP Tax report orchestration service).
// It is a separate backend from the business/reporting APIs, but shares the same transport + Cognito
// auth (the orchestrator API Gateway reuses the website's Cognito user pool), so this mirrors
// _callReportingApi exactly. The orchestrator speaks restJson1 with @http POST /OpName routes.
async function _callOrchestratorApi(method: string, endpoint: string, body: any | undefined = null, headers = {}) {
    if (environment === CONSTANTS.PRODUCTION) {
        orchestratorBaseUrl = CONSTANTS.ENVIRONMENT_VARIABLES.ORCHESTRATOR_API as string;
    }
    const url = `${orchestratorBaseUrl}${endpoint}`;

    const result = await _callExternalApi(method, url, body, headers);

    return result;
}

function flattenCliReportingConfig(config: any): any {
    if (!config) return config;
    const { testedPartyAttributes, ...rest } = config;
    return { ...rest, ...(testedPartyAttributes ?? {}) };
}

async function _downloadFromS3(s3Bucket: string, s3key: string) {
    // TODO - move S3 region to CloudFormation params
    const s3 = new AWS.S3({
        region: 'us-east-1'
    });
    const params = {
        Bucket: s3Bucket,
        Key: s3key,
    };
    const data = await s3.getObject(params).promise();
    return data.Body != undefined ? JSON.parse(data.Body.toString()) : null;
}

async function _uploadToS3(s3Bucket: string, s3key: string, file: File) {
    const s3 = new AWS.S3();
    const params = {
        Bucket: s3Bucket,
        Key: s3key,
        Body: file,
        ContentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };
    try{
        const status = await s3.upload(params).promise();
        return `SUCCESS: ${status.Key}`;
    }catch(ex){
        if (ErrorUtils.getMessage(ex).startsWith('Missing credentials')) {
            throw new Error('Credentials timed out due to inactivity. Please refresh the page and try again.')
        } else {
            throw ex;
        }
    }
}

const API = {
    async callExternalApi(method: string, url: string, body: any | undefined = null, headers = {}) {
        return _callExternalApi(method, url, body, headers);
    },
    async uploadToS3(s3Bucket: string, s3key: string, file: File){
        return _uploadToS3(s3Bucket,s3key,file);
    },
    async downloadFromS3(s3Bucket: string, s3Key: string) {
        return _downloadFromS3(s3Bucket, s3Key);
    },
    async getAllAgreements() {
        const result = await _callBusinessApi('GET', '/lov/agreement-numbers');
        return result;
    },
    async getAgreementStatusCategories() {
        const result = await _callBusinessApi('GET', '/lov/agreement-status-categories');
        return result;
    },
    async getAgreementLines(agreementNumber: string) {
        const result = await _callBusinessApi('GET', `/lov/agreement-line-numbers/${agreementNumber}`);
        return result;
    },
    async getAgreementDetails(agreement: string) {
        const result = await _callBusinessApi('GET', `/agreements/${agreement}`);
        return result;
    },
    async getAgreementVersions(recordId: string) {
        const result = await _callBusinessApi('GET', `/agreements/${recordId}/versions`);
        return result;
    },
    async getCalculationLines(agreementLineNumber: string) {
        const result = await _callBusinessApi('GET', `/lov/calculation-numbers/${agreementLineNumber}`);
        return result;
    },
    async getCLIDetails(recordId: string) {
        const result = await _callBusinessApi('GET', `/cli/${recordId}`);
        return result;
    },
    async getCLIDetailsForCalculationNumber(calculationNumber?: string) {
        const result = await _callBusinessApi('GET', `/cliDetails/${calculationNumber}`);
        return result;
    },
    async getCLIs(status?: string, workbookId?: string, calculationStatus?: string) {
        const parameters = (workbookId == null ? '' : `workbookId=${workbookId}&`)
            .concat(status == null ? '' : `status=${status}&`)
            .concat(calculationStatus == null ? '' : `calculationStatus=${calculationStatus}`);
        const queryString = parameters == '' ? '' : `?${parameters}`
        const result = await _callBusinessApi('GET', `/clis${queryString}`);
        return result;
    },
    async searchCalculations(payload: any) {
        const result = await _callBusinessApi('POST', '/search/cli', payload);
        return result;
    },
    async searchAgreements(payload: any) {
        const result = await _callBusinessApi('POST', '/search/agreements', payload);
        return result;
    },
    async searchICRS(payload: any) {
        const result = await _callBusinessApi('POST', '/lov/icrs', payload);
        return result;
    },
    async getUserProfile(userName: string) {
        const result = await _callBusinessApi('GET', `/user-profiles/${userName}`);
        return result;
    },
    async saveUserProfile(payload: any) {
        const result = await _callBusinessApi('PUT', '/user-profiles', payload);
        return result;
    },
    async getCalculationStatuses() {
        const result = await _callBusinessApi('GET', '/lov/calculation-statuses');
        return result;
    },
    async getAccountingOwners() {
        const result = await _callBusinessApi('GET', '/lov/accounting-owners');
        return result;
    },
    async getTaxOwners() {
        const result = await _callBusinessApi('GET', '/lov/tax-owners');
        return result;
    },
    async getDefaultWorklists() {
        const result = await _callBusinessApi('GET', '/lov/default-worklists');
        return result;
    },
    async getDataSourceTypes() {
        const result = await _callBusinessApi('GET', '/lov/data-source-types');
        return result;
    },
    async getPeriods() {
        const result = await _callBusinessApi('GET', '/lov/periods');
        return result;
    },
    async getCurrencyCodes() {
        const result = await _callBusinessApi('GET', '/lov/currency-codes');
        return result;
    },
    async getCurrencyConversionTypes() {
        const result = await _callBusinessApi('GET', '/lov/currency-conversion-types');
        return result;
    },
    async getCoaTypes() {
        const result = await _callBusinessApi('GET', '/lov/coa-types');
        return result;
    },
    async getCoaData(segment: string) {
        const result = await _callBusinessApi('GET', `/lov/coa-data/${segment}`);
        return result;
    },
    async getCalculationVersions(calculationNumber: string) {
        const result = await _callBusinessApi('GET', `/calculations/${calculationNumber}/versions`);
        return result;
    },
    async getCalculationMetadata(calculationNumber: string, version: number) {
        const result = await _callBusinessApi('GET', `/calculations/${calculationNumber}/versions/${version}`);
        return result;
    },
    async getWorkbooks() {
        const result = await _callBusinessApi('GET', '/workbooks');
        return result;
    },
    async getTPEUsers() {
        const result = await _callBusinessApi('GET', '/users');
        return result;
    },
    async updateCLICalcAssignee(payload: any) {
        const result = await _callBusinessApi('PUT', `/cli/${payload.cliRecordId}/calc-assignee`, payload);
        return result;
    },
    async updateCLICalcBuilder(payload: any) {
        const result = await _callBusinessApi('PUT', `/cli/${payload.cliRecordId}/calc-builder`, payload);
        return result;
    },
    async updateCLIWorkbook(payload: any) {
        const result = await _callBusinessApi('PUT', `/cli/${payload.cliRecordId}/workbook`, payload);
        return result;
    },
    async getCalcBuilderDataSources(calculationNumber: string, version: number, calculationExecutionId?: string) {
        const result = await _callBusinessApi('GET', `/calculations/${calculationNumber}/version/${version}/data-sources${calculationExecutionId == null ? '' : '?calculationExecutionId=' + calculationExecutionId}`);
        return result;
    },
    async createDataSourceRecord(calculationNumber:string, payload: any) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/data-sources`, payload);
        return result;
    },
    async updateDataSourceRecord(calculationNumber:string, payload: any) {
        const result = await _callBusinessApi('PUT', `/calculations/${calculationNumber}/data-sources`, payload);
        return result;
    },
    async deleteDataSourceRecord(calculationNumber: string, datasourceId: string) {
        const result = await _callBusinessApi('DELETE', `/calculations/${calculationNumber}/data-sources/${datasourceId}`, {});
        return result;
    },
    async createCalculation(payload: any) {
        const result = await _callBusinessApi('POST', `/calculations`, payload);
        return result;
    },
    async createCalculationTemplate(payload: any) {
        const result = await _callBusinessApi('POST', `/templates`, payload);
        return result;
    },
    async renameCalculationTemplate(templateId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/rename`, payload);
        return result;
    },
    async getCalcSteps(calculationNumber: string, version: number, calculationExecutionId?: string) {
        const result = await _callBusinessApi('GET', `/calculations/${calculationNumber}/versions/${version}/steps${calculationExecutionId == null ? '' : '?calculationExecutionId=' + calculationExecutionId}`);
        return result;
    },
    async getJeTags(calculationNumber: string, version: number) {
        const result = await _callBusinessApi('GET', `/je-tags/${calculationNumber}/version/${version}`);
        return result;
    },
    async createCalculationStep(calculationNumber: string, payload: any) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/steps`, payload);
        return result;
    },
    async updateCalculationStep(calculationNumber: string, stepId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/calculations/${calculationNumber}/steps/${stepId}`, payload);
        return result;
    },
    async deleteCalculationStep(calculationNumber: string, stepId: string) {
        const result = await _callBusinessApi(
            'DELETE',
            `/calculations/${calculationNumber}/steps/${stepId}`,
            {} as any // Doing this as the server expects a payload for DELETE method
        );
        return result;
    },
    async unTagJEFromCalculationStep(calculationNumber: string, stepId: string) {
        const result = await _callBusinessApi('PUT', `/calculations/${calculationNumber}/steps/${stepId}/unTagJE`, {} as any);
        return result;
    },
    async validateCalcSteps(calculationNumber: string) {
        const result = await _callBusinessApi('GET', `/calculations/${calculationNumber}/validate`);
        return result;
    },
    async cancelCalcStepsChanges(calculationNumber: string) {
        const result = await _callBusinessApi(
            'PATCH',
            `/calculations/${calculationNumber}/cancel`,
            {} as any // Doing this as the server expects a payload for PATCH method
        );
        return result;
    },
    async runCalculationSteps(calculationNumber: string, version: number, generateJournals: boolean) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/versions/${version}/run`, {generateJournals});
        return result;
    },
    async getCalcStepComments(calculationNumber: string, version: number, stepId: string) {
        const result = await _callBusinessApi('GET', `/calculations/${calculationNumber}/versions/${version}/steps/${stepId}/comments`);
        return result;
    },
    async createCalcStepComment(calculationNumber: string, version: number, stepId: string, comment: string) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/versions/${version}/steps/${stepId}/comments`, {
            calculationNumber,
            version,
            stepId,
            comment
        });
        return result;
    },
    async getDataSourceComments(calculationNumber: string, version: number, dataSourceId: string) {
        const result = await _callBusinessApi('GET', `/calculations/${calculationNumber}/versions/${version}/data-sources/${dataSourceId}/comments`);
        return result;
    },
    async createDataSourceComment(calculationNumber: string, version: number, datasourceId: string, comment: string) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/versions/${version}/data-sources/${datasourceId}/comments`, {
            calculationNumber,
            datasourceId,
            comment
        });
        return result;
    },
    async getLinkedCLIs(templateId: string) {
        const result = await _callBusinessApi('GET', `/templates/${templateId}/calculations`);
        return result;
    },
    async unlinkCalculationFromTemplate(calculationNumber: string, templateId: string) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/calculations/${calculationNumber}/unlink`, {calculationNumber, templateId});
        return result;
    },
    async getTemplates(workbookId?: string) {
        if (workbookId != null) {
            const result = await _callBusinessApi('GET', `/templates?workbookId=${workbookId}`);
            return result;
        } else {
            const result = await _callBusinessApi('GET', '/templates');
            return result;
        }
    },
    async linkCalculationToTemplate(calculationNumber: string, templateId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/calculations/${calculationNumber}/link`, payload);
        return result;
    },
    async bulkLinkCalculationsToTemplate(templateId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/bulkLink`, payload);
        return result;
    },
    async cloneCalculation(calculationNumber: string, templateId: string) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/calculations/${calculationNumber}/clone`, {calculationNumber, templateId});
        return result;
    },
    async bulkCloneCalculation(templateId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/bulkClone`, payload);
        return result;
    },
    async editTemplate(templateId: string, calculationNumber: string) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/edit`, {templateId, calculationNumber});
        return result;
    },
    async saveTemplate(templateId: string) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/save`, {templateId});
        return result;
    },
    async cancelTemplateChanges(templateId: string) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/cancel`, {templateId});
        return result;
    },
    async getJournals(calculationNumber: string, version: number, journalLineType: string) {
        const result = await _callBusinessApi('GET', `/calculations/${calculationNumber}/versions/${version}/journals/${journalLineType}`);
        return result;
    },
    async getJournalsV2(calculationNumber: string, version: number, journalLineType: string) {
        const result = await _callBusinessApi('GET', `/v2/calculations/${calculationNumber}/versions/${version}/journals/${journalLineType}`);
        return result;
    },
    async downloadJournals(calculationNumber: string, version: number, journalLineType: string) {
        const result = await _callBusinessApi('GET', `/calculations/${calculationNumber}/versions/${version}/journals/${journalLineType}/download`);
        return result;
    },
    async uploadJETemplate(s3Key: string, calculationNumber: string) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/uploadJETemplate`, {'s3Key': s3Key});
        return result;
    },
    async updateJournals(calculationNumber: string, payload: any) {
        const result = await _callBusinessApi('PATCH', `/calculations/${calculationNumber}/journals`, payload);
        return result;
    },
    async uploadCustomDataTable(s3Key: string, newTable: boolean){
        const result = await _callBusinessApi('POST', '/custom-data-tables/upload', {'key': s3Key, 'newTable': newTable});
        return result;
    },
    async uploadCalculation(s3Key: string, calculationNumber: string){
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/upload-calculation`, {'key': s3Key, 'calculationNumber': calculationNumber});
        return result;
    },
    async searchCustomDataTables(payload: any) {
        const result = await _callBusinessApi('POST', '/search/custom-data-tables', payload);
        return result;
    },
    async searchCustomDataTableRows(payload: any) {
        const result = await _callBusinessApi('POST', '/search/custom-data-table-rows', payload);
        return result;
    },
    async createCustomDataTable(tableName: string) {
        const result = await _callBusinessApi('POST', '/create/custom-data-tables', {tableName: tableName});
        return result;
    },
    async renameCustomDataTable(tableId: string, newTableName: string) {
        const result = await _callBusinessApi('POST', '/custom-data-tables/rename', {tableId, newTableName});
        return result;
    },
    async downloadFile(s3Bucket: string, s3Key: string) {
        const result = await _callBusinessApi('POST', '/download-file', {bucket: s3Bucket, key: s3Key});
        return result;
    },
    async saveCustomDataTable(tableId: string | null, rows: CustomDataTableRecord[], validate: boolean) {
        const result = await _callBusinessApi('POST', '/custom-data-tables/save', {tableId, rows, validate});
        return result;
    },
    async uploadCustomCOADefinition(key: string, coaSegment: string, newTable: boolean) {
        const result = await _callBusinessApi('POST', '/custom-coa-definitions/upload', {key, coaSegment, newTable});
        return result;
    },
    async createCustomCOADefinition(tableName: string, coaSegment: string) {
        const result = await _callBusinessApi('POST', '/custom-coa-definitions/create', {tableName, coaSegment});
        return result;
    },
    async renameCustomCOADefinition(tableId: string, newTableName: string) {
        const result = await _callBusinessApi('POST', '/custom-coa-definitions/rename', {tableId, newTableName});
        return result;
    },
    async saveCustomCOADefinition(tableId: string, rows: CustomCOARow[]) {
        const result = await _callBusinessApi('POST', '/custom-coa-definitions/save', {tableId, rows});
        return result;
    },
    async getCustomCOADefinitionsForSegment(coaSegment: string) {
        const result = await _callBusinessApi('GET', `/custom-coa-definitions-for-segment/${coaSegment}`);
        return result;
    },
    async getClassifications(tableId: string) {
        const result = await _callBusinessApi('GET', `/custom-coa-definitions/${tableId}/classifications`);
        return result;
    },
    async searchCustomCoaDefinitions(payload: any) {
        const result = await _callBusinessApi('POST', '/search/custom-coa-definitions', payload);
        return result;
    },
    async searchCustomCoaDefinitionRows(payload: any) {
        const result = await _callBusinessApi('POST', '/search/custom-coa-definition-rows', payload);
        return result;
    },
    async dryRunCalculation(calculationNumber: string, calculationVersion: number, generateTaxJournals: boolean) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/versions/${calculationVersion}/dry-run`, {
            calculationNumber,
            calculationVersion,
            generateTaxJournals
        });
        return result;
    },
    async getCalculationExecution(atpCalculationId: string, period: string) {
        const result = await _callBusinessApi('GET', `/calculation-executions/${atpCalculationId}/${period}`);
        return result;
    },
    async getJournalEntries(calculationExecutionId: string) {
        const result = await _callBusinessApi('GET', `/journalEntries/${calculationExecutionId}`);
        return result;
    },
    async getJournalEntriesV2(calculationExecutionId: string) {
        const result = await _callBusinessApi('GET', `/v2/journalEntries/${calculationExecutionId}`);
        return result;
    },
    async downloadCustomDataTable(tableId: string) {
        const result = await _callBusinessApi('POST', '/custom-data-tables/download', {tableId});
        return result;
    },
    async downloadCustomCOADefinition(tableId: string) {
        const result = await _callBusinessApi('POST', '/custom-coa-definitions/download', {tableId});
        return result;
    },
    async discardDraft(calculationNumber: string) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/discard-draft`, {calculationNumber});
        return result;
    },
    async getCustomDataTableAllowDeleteFlag(tableId: string) {
        const result = await _callBusinessApi('GET', `/custom-data-tables/${tableId}/allow-delete-flag`);
        return result;
    },
    async deleteCustomDataTable(tableId: string) {
        const result = await _callBusinessApi('DELETE', `/custom-data-tables/${tableId}`, {tableId});
        return result;
    },
    async archiveCustomDataTable(tableId: string) {
        const result = await _callBusinessApi('POST', '/custom-data-tables/archive', {tableId});
        return result;
    },

    // --- Segment-Level Custom Data Tables ---
    // All POST, matching the rest of this API. `period` is `yyyyMM` and mandatory wherever it appears.
    // These return the raw Response like every other method here, so callers parse and annotate:
    //   const body: GetSegmentDataTableResponse = await apiCallService.getSegmentDataTable(id).json();

    async createSegmentDataTable(payload: CreateSegmentDataTableRequest) {
        const result = await _callBusinessApi('POST', '/create/segment-data-table', payload);
        return result;
    },
    async getSegmentDataTable(tableId: string) {
        const result = await _callBusinessApi('POST', '/get/segment-data-table', {tableId});
        return result;
    },
    async listSegmentDataTables(payload: ListSegmentDataTablesRequest = {}) {
        const result = await _callBusinessApi('POST', '/list/segment-data-tables', payload);
        return result;
    },
    async searchSegmentDataTableRows(payload: SearchSegmentDataTableRowsRequest) {
        const result = await _callBusinessApi('POST', '/search/segment-data-table-rows', payload);
        return result;
    },
    async getSegmentAggregates(tableId: string, period: string) {
        const result = await _callBusinessApi('POST', '/segment-data-table/aggregates', {tableId, period});
        return result;
    },
    async getSegmentValidations(tableId: string, period: string) {
        const result = await _callBusinessApi('POST', '/segment-data-table/validations', {tableId, period});
        return result;
    },
    async getSegmentPeriods(tableId: string) {
        const result = await _callBusinessApi('POST', '/segment-data-table/periods', {tableId});
        return result;
    },
    async getCDTGLPullStatus(pullRequestId?: string, tableId?: string, period?: string) {
        // GetCDTGLPullStatus identifies the pull one of two ways: by pullRequestId (the caller polling
        // the pull it just triggered), or by tableId + period (a reload that no longer holds the id —
        // reports whichever pull is in flight for that table/period). Exactly one form is sent.
        const payload = pullRequestId ? {pullRequestId} : {tableId, period};
        const result = await _callBusinessApi('POST', '/segment-data-table/gl-pull-status', payload);
        return result;
    },
    async downloadSegmentDataTable(tableId: string, period: string) {
        const result = await _callBusinessApi('POST', '/segment-data-table/download', {tableId, period});
        return result;
    },
    /** Presigned URL for the prefilled upload template (manual columns only), an input to the upload op. */
    async downloadSegmentUploadTemplate(tableId: string, period: string) {
        const result = await _callBusinessApi('POST', '/segment-data-table/upload-template', {tableId, period});
        return result;
    },
    async saveSegmentDataTable(tableId: string, period: string, rows: SegmentRow[]) {
        const result = await _callBusinessApi('POST', '/segment-data-table/save', {tableId, period, rows});
        return result;
    },
    /** `s3Key` points at a file the caller has already uploaded to S3. */
    async uploadSegmentDataTable(tableId: string, s3Key: string) {
        // The model is { tableId, s3Key } — the period is resolved server-side (the current open period)
        // and echoed back in the response, not sent in the request.
        const result = await _callBusinessApi('POST', '/segment-data-table/upload', {tableId, s3Key});
        return result;
    },
    /** Full-table GL pull. Returns 202 with a `pullRequestId` to poll for progress. */
    async triggerCDTGLPull(tableId: string, period: string) {
        const result = await _callBusinessApi('POST', '/segment-data-table/trigger-gl-pull', {tableId, period});
        return result;
    },
    async getCustomCOADefinitionAllowDeleteFlag(tableId: string) {
        const result = await _callBusinessApi('GET', `/custom-coa-definitions/${tableId}/allow-delete-flag`);
        return result;
    },
    async deleteCustomCOADefinition(tableId: string) {
        const result = await _callBusinessApi('DELETE', `/custom-coa-definitions/${tableId}`, {tableId});
        return result;
    },
    async archiveCustomCOADefinition(tableId: string) {
        const result = await _callBusinessApi('POST', '/custom-coa-definitions/archive', {tableId});
        return result;
    },
    async getReportingPeriods() {
        const result = await _callReportingApi('GET', '/GetReportingPeriods');
        return result;
    },
    async generateReport(payload: any) {
        const result = await _callReportingApi('POST', '/GenerateReport', payload);
        return result;
    },
    // Lists the report-generation runs (versions) for a period from the ICTPReportingOrchestrator
    // GetTPReportVersions op (restJson1 POST). Separate backend from the reporting API above.
    async getTPReportVersions(period: string) {
        const result = await _callOrchestratorApi('POST', '/GetTPReportVersions', { period });
        return result;
    },
    // Kicks off an on-demand full TP Tax report refresh for a period via the ICTPReportingOrchestrator
    // TriggerOnDemandFullRefresh op (restJson1 POST). period is yyyyMM.
    async triggerOnDemandFullRefresh(period: string) {
        const result = await _callOrchestratorApi('POST', '/TriggerOnDemandFullRefresh', { period });
        return result;
    },
    async listReports(username?: string) {
        if (username != null) {
            const result = await _callReportingApi('GET', `/ListReports?userId=${username}`);
            return result;
        } else {
            const result = await _callReportingApi('GET', `/ListReports`);
            return result;
        }
    },
    async downloadReport(payload: any) {
        const result = await _callReportingApi('POST', '/DownloadReport', payload);
        return result;
    },
    async searchTaxAuditRecords(payload: any) {
        const result = await _callReportingApi('POST', '/SearchTaxAuditRecords', payload);
        return result;
    },
    async getCLIExecutionRecords(payload: any) {
        const result = await _callReportingApi('POST', '/GetCLIExecutionRecords', payload);
        return result;
    },
    async downloadCLIExecutionRecords(periodId: string) {
        const result = await _callReportingApi('GET', `/DownloadCLIExecutionRecords?periodId=${periodId}`);
        return result;
    },
    async getTaxAuditRecordDetails(taxJournalHeaderId: string) {
        const result = await _callReportingApi('GET', `/GetTaxAuditRecordDetails/${taxJournalHeaderId}`);
        return result;
    },
    async downloadTaxAuditRecord(payload: any) {
        const result = await _callReportingApi('POST', '/DownloadTaxAuditRecord', payload);
        return result;
    },
    async fetchIndirectTaxChangeRecords(payload: any) {
        const result = await _callReportingApi('POST', '/FetchIndirectTaxChangeRecords', payload);
        return result;
    },
    async fetchTPTaxVarianceRecords(payload: any) {
        const result = await _callReportingApi('POST', '/GetTPTaxVarianceEntries', payload);
        return result;
    },
    async getTPReportFilters(payload: any) {
        const result = await _callReportingApi('POST', '/GetTPReportFilters', payload);
        return result;
    },
    async getFunctionalSegmentCategoriesByEntity(payload: any) {
        const result = await _callReportingApi('POST', '/GetFunctionalSegmentCategoriesByEntity', payload);
        return result;
    },
    async downloadTPTaxMultiCLIReport(payload: any) {
        const result = await _callReportingApi('POST', '/DownloadTPTaxMultiCLIReport', payload);
        return result;
    },
    async getAccountMapping(mappingType: string, exceptionMappingFor?: string) {
        if (exceptionMappingFor) {
            const result = await _callReportingApi('GET', `/accountMapping?mappingType=${mappingType}&exceptionMappingFor=${exceptionMappingFor}`);
            return result;
        } else {
            const result = await _callReportingApi('GET', `/accountMapping?mappingType=${mappingType}`);
            return result;
        }
    },
    async getFiscalYearActiveCLIsForWorkbook(payload: any) {
        const result = await _callReportingApi('POST', '/GetFiscalYearActiveClisForWorkbook', payload);
        return result;
    },
    async updateAccountMapping(payload: any) {
        const result = await _callReportingApi('POST', '/UpdateAccountMapping', payload);
        return result;
    },
    async getAccountMappingActivityLogs() {
        const result = await _callReportingApi('GET', '/GetAccountMappingActivityLogs');
        return result;
    },
    async fetchTPReportsSummary(payload: any) {
        const result = await _callReportingApi('POST', '/GetTPReportsSummary', payload);
        return result;
    },
    async fetchReportGenerationSummary(payload: any) {
        const result = await _callReportingApi('POST', '/GetReportGenerationSummary', payload);
        return result;
    },
    async getCOADataBySegment(payload: string) {
        const result = await _callReportingApi('GET', `/CoaData/${payload}`);
        return result;
    },
    async downloadIndirectTaxChangeRecords(reportId: string) {
        const result = await _callReportingApi('GET', `/DownloadIndirectTaxChangeRecords?reportId=${reportId}`);
        return result;
    },
    async getTPEntries(calculationNumber: string, ytdIndia: boolean) {
        const result = await _callBusinessApi('POST', `/lov/tp-entries/${calculationNumber}`, {calculationNumber, ytdIndia});
        return result;
    },
    async submitCalculationForReview(calculationNumber: string, payload: any) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/submit-for-review`, payload);
        return result;
    },
    async getCLIExecutionGroups() {
        const result = await _callBusinessApi('GET', `/lov/cli-execution-groups/`);
        return result;
    },
    async saveCalculationAttribute(calculationNumber: string, payload: any) {
        const result = await _callBusinessApi('PATCH', `/calculations/${calculationNumber}/attribute`, payload);
        return result;
    },
    async getJEStatuses() {
        const result = await _callBusinessApi('GET', '/lov/je-statuses/');
        return result;
    },
    async rejectCalculationDraft(calculationNumber: string, payload: any) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/reject-draft`, payload);
        return result;
    },
    async approveCalculation(calculationNumber: string) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/approve`, {calculationNumber});
        return result;
    },
    async getCLIsByProviderCompanyCode(providerCompanyCode: string, calculationStatus: string){
        const result = await _callReportingApi('GET', `/GetCLIsByProvider?providerCompanyCode=${providerCompanyCode}&calculationStatus=${calculationStatus}`)
        return result;
    },
    async saveStandardAllocation(templateId: string, calculationNumber: string, payload: any) {
        const result = await _callBusinessApi('POST', `/templates/${templateId}/calculations/${calculationNumber}/standard-allocation`, payload);
        return result;
    },
    async deleteDatasourceStandardAllocation(templateId: string, datasourceId: string) {
        const result = await _callBusinessApi('DELETE', `/templates/${templateId}/data-sources/${datasourceId}/standard-allocation`, {});
        return result;
    },
    async deleteStepStandardAllocation(templateId: string, stepId: string) {
        const result = await _callBusinessApi('DELETE', `/templates/${templateId}/steps/${stepId}/standard-allocation`, {});
        return result;
    },
    async fetchReportData(payload: any) {
        const result = await _callReportingApi('POST', '/FetchReportData', payload);
        return result;
    },
    async getAllCliReportingConfig(configType: string) {
        const result: any = await _callReportingApi('GET', `/reporting/cli/configType/${configType}`);
        const response = await result.json();
        const downloadUrl = response?.downloadUrl;
        if (!downloadUrl) {
            throw new Error('getAllCliReportingConfig: no downloadUrl in response');
        }
        const s3Response = await fetch(downloadUrl);
        if (!s3Response.ok) {
            throw new Error(`getAllCliReportingConfig: S3 fetch failed with status ${s3Response.status}`);
        }
        const data = await s3Response.json();
        const configs = (data?.cliReportingConfigs ?? []).map(flattenCliReportingConfig);
        return { CliReportingConfigs: configs };
    },
    async getCliReportingConfig(cliNumber: string, configType: string, version?: string) {
        const versionParam = version ? `?version=${version}` : '';
        const result: any = await _callReportingApi('GET', `/cli/${cliNumber}/configType/${configType}${versionParam}`);
        const response = await result.json();
        if (!version) {
            const configs = (response?.cliReportingConfigs ?? []).map(flattenCliReportingConfig);
            return { CliReportingConfigs: configs };
        }
        const record = response?.cliReportingConfig ?? response?.CliReportingConfig ?? null;
        return { CliReportingConfig: record ? flattenCliReportingConfig(record) : null };
    },
    async addCliReportingConfig(operation: string, cliNumber: string, configType: string, payload: any) {
        const result: any = await _callReportingApi('POST', `/reporting/${operation}/cli/${cliNumber}/configType/${configType}`, payload);
        const response = await result.json();
        const record = response?.cliReportingConfig ?? response?.CliReportingConfig ?? null;
        return { cliReportingConfig: record ? flattenCliReportingConfig(record) : null };
    },
    async getCLIAPI() {
        const result = await _callBusinessApi('GET', '/clis?Active');
        return result;
    },
    async getAgreementStatus() {
        const result = await _callBusinessApi('GET', '/lov/agreement-status');
        return result;
    },
    async getCalculationStatus() {
        const result = await _callBusinessApi('GET', '/lov/calculation-statuses');
        return result;
    },
    async getGroupExecutionCLIDetails(groupNumber: string, period: string) {
        const result = await _callBusinessApi('GET', `/mec/group-execution-cli-details/${period}/${groupNumber}`);
        return result;
    },
    async getTPAllocationTemplatesAndWorksheets() {
        const result = await _callBusinessApi('GET', '/tp-allocation-templates-and-worksheets');
        return result;
    },
    async getTPAllocationWorksheetDetails(worksheetId: string) {
        const result = await _callBusinessApi('GET', `/worksheets/${worksheetId}`);
        return result;
    },
    async getTPAllocationWorksheetDataSources(worksheetId: string, worksheetVersion: number, executionPeriodId?: string) {
        const result = await _callBusinessApi('GET', `/worksheets/${worksheetId}/versions/${worksheetVersion}/data-sources?periodId=${executionPeriodId}`);
        return result;
    },
    async getTPAllocationWorksheetTotals(worksheetId: string, worksheetVersion: number, executionPeriodId?: string) {
        const result = await _callBusinessApi('GET', `/worksheets/${worksheetId}/versions/${worksheetVersion}/totals?periodId=${executionPeriodId}`);
        return result;
    },
    async getTPAllocationWorksheetGroups(worksheetId: string, worksheetVersion: number) {
        const result = await _callBusinessApi('GET', `/worksheets/${worksheetId}/versions/${worksheetVersion}/allocation-groups`);
        return result;
    },
    async getTPAllocationWorksheetGroupFormulas(worksheetId: string, worksheetVersion: number, allocationGroupId: string, executionPeriodId?: string) {
        const result = await _callBusinessApi('GET', `/worksheets/${worksheetId}/versions/${worksheetVersion}/allocation-groups/${allocationGroupId}/allocation-formulas?periodId=${executionPeriodId}`);
        return result;
    },
    async searchTPAllocationWorksheets(payload: any) {
        const result = await _callBusinessApi('POST', '/search/worksheets', payload);
        return result;
    },
    async searchTPAllocationWorksheetFormulas(payload: any) {
        const result = await _callBusinessApi('POST', '/search/worksheet-allocation-formulas', payload);
        return result;
    },
    async getTPAllocationWorksheetTemplateDetails(templateId: string) {
        const result = await _callBusinessApi('GET', `/worksheet-templates/${templateId}`);
        return result;
    },
    async getTPAllocationWorksheetTemplatePlaceholders(templateId: string, templateVersion: number) {
        const result = await _callBusinessApi('GET', `/worksheet-templates/${templateId}/versions/${templateVersion}/placeholders`);
        return result;
    },
    async getTPAllocationWorksheetTemplateDataSources(templateId: string, templateVersion: number) {
        const result = await _callBusinessApi('GET', `/worksheet-templates/${templateId}/versions/${templateVersion}/data-sources`);
        return result;
    },
    async getTPAllocationWorksheetTemplateTotals(templateId: string, templateVersion: number) {
        const result = await _callBusinessApi('GET', `/worksheet-templates/${templateId}/versions/${templateVersion}/totals`);
        return result;
    },
    async getTPAllocationWorksheetTemplateGroups(templateId: string, templateVersion: number) {
        const result = await _callBusinessApi('GET', `/worksheet-templates/${templateId}/versions/${templateVersion}/allocation-groups`);
        return result;
    },
    async getTPAllocationWorksheetTemplateGroupFormulas(templateId: string, templateVersion: number, allocationGroupId: string) {
        const result = await _callBusinessApi('GET', `/worksheet-templates/${templateId}/versions/${templateVersion}/allocation-groups/${allocationGroupId}/allocation-formulas`);
        return result;
    },
    async getTPAllocationWorksheetPlaceholders(worksheetId: string, worksheetVersion: number) {
        const result = await _callBusinessApi('GET', `/worksheets/${worksheetId}/versions/${worksheetVersion}/placeholders`);
        return result;
    },
    async createWorksheetDatasetRecord(worksheetId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/worksheets/${worksheetId}/data-sources`, payload);
        return result;
    },
    async updateWorksheetDatasetRecord(worksheetId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/worksheets/${worksheetId}/data-sources/${payload.dataSourceId}`, payload);
        return result;
    },
    async deleteWorksheetDatasetRecord(worksheetId: string, dataSourceId: string) {
        const result = await _callBusinessApi('DELETE', `/worksheets/${worksheetId}/data-sources/${dataSourceId}`,{worksheetId, dataSourceId});
        return result;
    },
    async createWorksheetTotal(worksheetId:string, payload: any) {
        const result = await _callBusinessApi('POST', `/worksheets/${worksheetId}/totals`, payload);
        return result;
    },
    async updateWorksheetTotal(worksheetId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/worksheets/${worksheetId}/totals/${payload.totalId}`, payload);
        return result;
    },
    async deleteWorksheetTotal(worksheetId: string, totalId: string) {
        const result = await _callBusinessApi('DELETE', `/worksheets/${worksheetId}/totals/${totalId}`,{worksheetId, totalId});
        return result;
    },
    async createWorksheetAllocationGroup(worksheetId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/worksheets/${worksheetId}/allocation-groups`, payload);
        return result;
    },
    async updateWorksheetAllocationGroup(worksheetId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/worksheets/${worksheetId}/allocation-groups/${payload.allocationGroupId}`, payload);
        return result;
    },
    async deleteWorksheetAllocationGroup(worksheetId: string, allocationGroupId: string) {
        const result = await _callBusinessApi('DELETE', `/worksheets/${worksheetId}/allocation-groups/${allocationGroupId}`,{worksheetId, allocationGroupId});
        return result;
    },
    async createWorksheetAllocationGroupFormula(worksheetId: string, allocationGroupId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/worksheets/${worksheetId}/allocation-groups/${allocationGroupId}/allocation-formulas`, payload);
        return result;
    },
    async updateWorksheetAllocationGroupFormula(worksheetId: string, allocationGroupId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/worksheets/${worksheetId}/allocation-groups/${allocationGroupId}/allocation-formulas/${payload.allocationFormulaId}`, payload);
        return result;
    },
    async deleteWorksheetAllocationGroupFormula(worksheetId: string, allocationGroupId: string, allocationFormulaId: string) {
        const result = await _callBusinessApi('DELETE', `/worksheets/${worksheetId}/allocation-groups/${allocationGroupId}/allocation-formulas/${allocationFormulaId}`,{worksheetId, allocationGroupId, allocationFormulaId});
        return result;
    },
    async createWorksheet(payload: any) {
        const result = await _callBusinessApi('POST', '/worksheets', payload);
        return result;
    },
    async createWorksheetTemplate(templateName: string) {
        const result = await _callBusinessApi('POST', '/worksheet-templates', { templateName });
        return result;
    },
    async createTPAllocationWorksheetTemplatePlaceholder(templateId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/worksheet-templates/${templateId}/placeholders`, payload);
        return result;
    },
    async updateTPAllocationWorksheetTemplatePlaceholder(templateId: string, placeholderId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/worksheet-templates/${templateId}/placeholders/${placeholderId}`, payload);
        return result;
    },
    async deleteTPAllocationWorksheetTemplatePlaceholder(templateId: string, placeholderId: string) {
        const result = await _callBusinessApi('DELETE', `/worksheet-templates/${templateId}/placeholders/${placeholderId}`, {templateId, placeholderId});
        return result;
    },
    async submitWorksheet(worksheetId: string, notes: string) {
        const result = await _callBusinessApi('POST', `/worksheets/${worksheetId}/submit-for-review`, { worksheetId, notes });
        return result;
    },
    async approveWorksheet(worksheetId: string) {
        const result = await _callBusinessApi('POST', `/worksheets/${worksheetId}/approve`, { worksheetId });
        return result;
    },
    async rejectWorksheet(worksheetId: string, notes: string) {
        const result = await _callBusinessApi('POST', `/worksheets/${worksheetId}/reject`, { worksheetId, notes });
        return result;
    },
    async validateWorksheet(worksheetId:string) {
        const result = await _callBusinessApi('GET', `/worksheets/${worksheetId}/validate`);
        return result;
    },
    async createWorksheetTemplateDatasetRecord(templateId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/worksheet-templates/${templateId}/data-sources`, payload);
        return result;
    },
    async updateWorksheetTemplateDatasetRecord(templateId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/worksheet-templates/${templateId}/data-sources/${payload.dataSourceId}`, payload);
        return result;
    },
    async deleteWorksheetTemplateDatasetRecord(templateId: string, dataSourceId: string) {
        const result = await _callBusinessApi('DELETE', `/worksheet-templates/${templateId}/data-sources/${dataSourceId}`,{templateId, dataSourceId});
        return result;
    },
    async createWorksheetTemplateTotal(templateId:string, payload: any) {
        const result = await _callBusinessApi('POST', `/worksheet-templates/${templateId}/totals`, payload);
        return result;
    },
    async updateWorksheetTemplateTotal(templateId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/worksheet-templates/${templateId}/totals/${payload.totalId}`, payload);
        return result;
    },
    async deleteWorksheetTemplateTotal(templateId: string, totalId: string) {
        const result = await _callBusinessApi('DELETE', `/worksheet-templates/${templateId}/totals/${totalId}`,{templateId, totalId});
        return result;
    },
    async createWorksheetTemplateAllocationGroup(templateId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/worksheet-templates/${templateId}/allocation-groups`, payload);
        return result;
    },
    async updateWorksheetTemplateAllocationGroup(templateId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/worksheet-templates/${templateId}/allocation-groups/${payload.allocationGroupId}`, payload);
        return result;
    },
    async deleteWorksheetTemplateAllocationGroup(templateId: string, allocationGroupId: string) {
        const result = await _callBusinessApi('DELETE', `/worksheet-templates/${templateId}/allocation-groups/${allocationGroupId}`,{templateId, allocationGroupId});
        return result;
    },
    async createWorksheetTemplateAllocationGroupFormula(templateId: string, allocationGroupId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/worksheet-templates/${templateId}/allocation-groups/${allocationGroupId}/allocation-formulas`, payload);
        return result;
    },
    async updateWorksheetTemplateAllocationGroupFormula(templateId: string, allocationGroupId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/worksheet-templates/${templateId}/allocation-groups/${allocationGroupId}/allocation-formulas/${payload.allocationFormulaId}`, payload);
        return result;
    },
    async deleteWorksheetTemplateAllocationGroupFormula(templateId: string, allocationGroupId: string, allocationFormulaId: string) {
        const result = await _callBusinessApi('DELETE', `/worksheet-templates/${templateId}/allocation-groups/${allocationGroupId}/allocation-formulas/${allocationFormulaId}`,{templateId, allocationGroupId, allocationFormulaId});
        return result;
    },
    async submitWorksheetTemplate(templateId: string, notes: string) {
        const result = await _callBusinessApi('POST', `/worksheet-templates/${templateId}/submit-for-review`, { templateId, notes });
        return result;
    },
    async approveWorksheetTemplate(templateId: string) {
        const result = await _callBusinessApi('POST', `/worksheet-templates/${templateId}/approve`, { templateId });
        return result;
    },
    async rejectWorksheetTemplate(templateId: string, notes: string) {
        const result = await _callBusinessApi('POST', `/worksheet-templates/${templateId}/reject`, { templateId, notes });
        return result;
    },
    async getMECStatusData(period: string) {
        const result = await _callBusinessApi('GET', `/mec/latest-group-execution-status/${period}`);
        return result;
    },
    async getBulkReversals(period: string) {
        const result = await _callBusinessApi('GET', `/mec/bulk-reversals/${period}`);
        return result;
    },
    async searchItemForReversal(period: string, workbookId?: string, calculationNumber?: string) {
        const result = await _callBusinessApi('POST', '/search/mec/item-for-reversal', { period, workbookId, calculationNumber });
        return result;
    },
    async getLastGLDataIngestionTimestamp() {
        const result = await _callBusinessApi('GET', '/mec/last-gl-data-ingestion-timestamp');
        return result;
    },
    async getICRegistration(registrationNumber: string) {
        const result = await _callBusinessApi('GET', `/icrs/${registrationNumber}`);
        return result;
    },
    async getLatestWorksheetExecutionVersion(worksheetId: string, periodId: string) {
        const result = await _callBusinessApi('GET', `/worksheets/${worksheetId}/executions/${periodId}`);
        return result;
    },
    async getOrAssignExecutionGruop(calculationNumber: string) {
        const result = await _callBusinessApi('POST', `/calculations/${calculationNumber}/execution-group`, { calculationNumber });
        return result;
    },
    async saveCalculationExecutionGroup(calculationNumber: string, executionGroup: string) {
        const result = await _callBusinessApi('PATCH', `/calculations/${calculationNumber}/execution-group`, { calculationNumber, executionGroup });
        return result;
    },
    async getPeriodCharges(calculationNumber: string) {
        const result = await _callBusinessApi('GET', `/period-charges/${calculationNumber}`);
        return result;
    },
    async savePeriodChargeAdjustment(calculationNumber: string, payload: any) {
        const result = await _callBusinessApi('POST', `/period-charges/${calculationNumber}`, payload);
        return result;
    },
    async uploadPeriodCharges(key: string) {
        const result = await _callBusinessApi('PUT', '/period-charges', { s3Key: key});
        return result;
    },
    async getHandOffStatus() {
        const result = await _callBusinessApi('GET', `/mec/get-mec-hand-off-status`);
        return result;
    },
    async mecHandOff() {
        const result = await _callBusinessApi('POST', `/mec/mec-hand-off`, {});
        return result;
    },
    async mecSoftClose() {
        const result = await _callBusinessApi('POST', `/mec/mec-hand-off`, { isSoftClose: true });
        return result;
    },
    async getNotifications() {
        const result = await _callBusinessApi('GET', `/notifications`, {});
        return result;
    },
    async searchOpenCalculationReviews(payload: any) {
        const result = await _callBusinessApi('POST', '/search/open-calculation-reviews', payload);
        return result;
    },
    async getNotificationSummary() {
        const result = await _callBusinessApi('GET', '/notification-summary');
        return result;
    },
    async searchApprovals(payload: any) {
        const result = await _callBusinessApi('POST', '/search/approval-notifications', payload);
        return result;
    },
    async searchRequests(payload: any) {
        const result = await _callBusinessApi('POST', '/search/request-notifications', payload);
        return result;
    },
    async searchTasks(payload: any) {
        const result = await _callBusinessApi('POST', '/search/task-notifications', payload);
        return result;
    },
    async markNotificationsComplete(payload: any) {
        const result = await _callBusinessApi('PUT', '/notifications/mark-complete', payload);
        return result;
    },
    async getBeginningBalanceTasks(calculationNumber: string) {
        const result = await _callBusinessApi('GET', `/notifications/tasks/beginning-balance?calculationNumber=${calculationNumber}`);
        return result;
    },
    async saveNotificationComment(payload: any) {
        const result = await _callBusinessApi('POST', '/notifications/save-comment', payload);
        return result;
    },
    async getCLIReviewNotificationComments(calculationNumber?: string) {
        const result = await _callBusinessApi('GET', `/notifications/${calculationNumber}/comments`);
        return result;
    },
    async getJEAllocationTemplatesAndWorksheets() {
        const result = await _callBusinessApi('GET', '/je-allocation-templates-and-worksheets');
        return result;
    },
    async createJEAllocationWorksheet(payload: any) {
        const result = await _callBusinessApi('POST', '/je-worksheet', payload);
        return result;
    },
    async createJEAllocationWorksheetTemplate(templateName: string) {
        const result = await _callBusinessApi('POST', '/je-worksheet-template', { templateName });
        return result;
    },
    async getPLIConfigMetadata(calculationNumber: string) {
        const result = await _callBusinessApi('GET', `/pli/${calculationNumber}`);
        return result;
    },
    async getPLIConfigSteps(calculationNumber: string, pliVersion: number) {
        const result = await _callBusinessApi('GET', `/pli/${calculationNumber}/versions/${pliVersion}/steps`);
        return result;
    },
    async createPLIConfig(payload: any) {
        const result = await _callBusinessApi('POST', "/pli", payload);
        return result;
    },
    async savePLIConfigOverviewSectionStep(calculationNumber: string, payload: any) {
        const result = await _callBusinessApi('PATCH', `/pli/${calculationNumber}/overviewSectionStep`, payload);
        return result;
    },
    async savePLIConfigStep(calculationNumber: string, stepId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/pli/${calculationNumber}/steps/${stepId}`, payload);
        return result;
    },
    async createPLIConfigStep(calculationNumber: string, payload: any) {
        const result = await _callBusinessApi('POST', `/pli/${calculationNumber}/steps`, payload);
        return result;
    },
    async validatePLIConfig(calculationNumber: string, calculationVersion: number) {
        const result = await _callBusinessApi('GET', `/pli/${calculationNumber}/validate/${calculationVersion}`);
        return result;
    },
    async runPLIConfig(calculationNumber: string, pliVersion: number) {
        const result = await _callBusinessApi('POST', `/pli/${calculationNumber}/versions/${pliVersion}/run`, {calculationNumber, pliVersion});
        return result;
    },
    async deletePLIConfigStep(calculationNumber: string, stepId: string) {
        const result = await _callBusinessApi('DELETE', `/pli/${calculationNumber}/steps/${stepId}`, {calculationNumber, stepId});
        return result;
    },
    async configureSingleCLI(calculationNumber: string) {
        const result = await _callBusinessApi('POST', `/pli/${calculationNumber}/save`, {calculationNumber});
        return result;
    },
    async clonePLIConfiguration(calculationNumber: string, payload: string) {
        const result = await _callBusinessApi('POST', `/pli/${calculationNumber}/clone`, payload);
        return result;
    },
    async getPLITemplateTypes(calculationNumber: string) {
        const result = await _callBusinessApi('GET', `/pli/${calculationNumber}/allTypes`);
        return result;
    },
    async switchPLITemplate(calculationNumber: string, newPLI: string) {
        const result = await _callBusinessApi('PUT', `/pli/${calculationNumber}/switch`, {calculationNumber, newPLI});
        return result;
    },
    async getMultiAllocationWorksheets(status?: string, groupNumber?: string) {
        const queryParams = groupNumber ? `?group_number=${groupNumber}` : '';
        const result = await _callBusinessApi('GET', `/multi-allocation/worksheets${queryParams}`);
        return result;
    },
    async createMultiAllocationWorksheet(payload: CreateMultiAllocationWorksheetRequest) {
        const result = await _callBusinessApi('POST', '/multi-allocation/worksheet', payload);
        return result;
    },
    async getMultiAllocationSheets(status?: string) {
        const queryParams = status ? `?status=${status}` : '';
        const result = await _callBusinessApi('GET', `/multi-allocation/sheets${queryParams}`);
        return result;
    },
    // ----- Common Balance Pool (MDA 1.1 Phase 2). Paths match the ICTransferPricingWebAPILambdaModel CBP contract. -----
    async getCommonBalancePools(grain?: string, status?: string) {
        const params = new URLSearchParams();
        if (grain) params.append('grain', grain);
        if (status) params.append('status', status);
        const qs = params.toString() ? `?${params.toString()}` : '';
        return _callBusinessApi('GET', `/common-balance-pool${qs}`);
    },
    async getCommonBalancePool(poolId: string) {
        return _callBusinessApi('GET', `/common-balance-pool/${encodeURIComponent(poolId)}`);
    },
    async createCommonBalancePool(payload: CreateCommonBalancePoolRequest) {
        return _callBusinessApi('POST', '/common-balance-pool', payload);
    },
    async updateCommonBalancePool(poolId: string, payload: UpdateCommonBalancePoolRequest) {
        return _callBusinessApi('PUT', `/common-balance-pool/${encodeURIComponent(poolId)}`, payload);
    },
    async validateCommonBalancePool(poolId: string, period?: string) {
        return _callBusinessApi('POST', `/common-balance-pool/${encodeURIComponent(poolId)}/validate`, period ? { period } : {});
    },
    async previewCommonBalancePool(poolId: string, period: string, rowLimit?: number) {
        return _callBusinessApi('POST', `/common-balance-pool/${encodeURIComponent(poolId)}/preview`,
            rowLimit != null ? { period, rowLimit } : { period });
    },
    async executeCommonBalancePool(poolId: string, period: string) {
        return _callBusinessApi('POST', `/common-balance-pool/${encodeURIComponent(poolId)}/execute`, { period });
    },
    async getCommonBalancePoolExecutionStatus(executionId: string) {
        return _callBusinessApi('GET', `/common-balance-pool-execution/${encodeURIComponent(executionId)}`);
    },
    async getCommonBalancePoolRows(poolId: string, period: string, pageSize?: number, nextToken?: string) {
        const params = new URLSearchParams();
        params.append('period', period);
        if (pageSize != null) params.append('pageSize', String(pageSize));
        if (nextToken) params.append('nextToken', nextToken);
        return _callBusinessApi('GET', `/common-balance-pool/${encodeURIComponent(poolId)}/rows?${params.toString()}`);
    },
    async getMultiAllocationSheet(sheetId: string, version?: number) {
        const queryParams = (version != null && version != undefined) ? `?version=${version}` : '';
        const result = await _callBusinessApi('GET', `/multi-allocation/sheet/${sheetId}${queryParams}`);
        return result;
    },
    async getMultiAllocationWorksheet(worksheetId: string, version?: number) {
        const versionParam = version !== undefined ? `?version=${version}` : '';
        const result = await _callBusinessApi('GET', `/multi-allocation/worksheet/${worksheetId}${versionParam}`);
        return result;
    },
    async createMultiAllocationWorksheetDataSource(worksheetId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/multi-allocation/worksheet/${worksheetId}/data-source`, payload);
        return result;
    },
    async updateMultiAllocationWorksheetDataSource(worksheetId: string, dataSourceId: string, payload: any) {
        const result = await _callBusinessApi('PUT', `/multi-allocation/worksheet/${worksheetId}/data-source/${dataSourceId}`, payload);
        return result;
    },
    async deleteMultiAllocationWorksheetDataSource(worksheetId: string, dataSourceId: string) {
        const result = await _callBusinessApi('DELETE', `/multi-allocation/worksheet/${worksheetId}/data-source/${dataSourceId}`, {worksheetId, dataSourceId});
        return result;
    },
    async getMultiAllocationWorksheetDataSources(worksheetId: string, worksheetVersion: number) {
        const result = await _callBusinessApi('GET', `/multi-allocation/worksheet/${worksheetId}/version/${worksheetVersion}/data-sources`);
        return result;
    },
    async createMultiAllocationSheet(payload: CreateMultiAllocationSheetRequest) {
        const result = await _callBusinessApi('POST', '/multi-allocation/sheet', payload);
        return result;
    },
    async executeMultiAllocationDataSource(worksheetId: string, dataSourceId: string, payload: any) {
        const result = await _callBusinessApi('POST', `/multi-allocation/worksheet/${worksheetId}/datasource/${dataSourceId}/execute`, payload);
        return result;
    },
    async executeMultiAllocationWorksheet(worksheetId: string, executionSource: string, payload: any) {
        const result = await _callBusinessApi('POST', `/multi-allocation/worksheet/${worksheetId}/execute/${executionSource}`, payload);
        return result;
    },
    async getMultiAllocationExecutionStatus(worksheetId: string, executionId?: string) {
        const queryParams = executionId ? `?executionId=${executionId}` : '';
        const result = await _callBusinessApi('GET', `/multi-allocation/worksheet/${worksheetId}/execution-status${queryParams}`);
        return result;
    },
    async updateMultiAllocationSheet(payload: UpdateMultiAllocationSheetRequest) {
        const result = await _callBusinessApi('PUT', `/multi-allocation/sheet/${payload.sheetId}`, payload);
        return result;
    },
    async cloneMultiAllocationWorksheet(payload: any) {
        const result = await _callBusinessApi('POST', '/multi-allocation/worksheet-clone', payload);
        return result;
    },
    async processMultiAllocationApprovals(approvalType: string, approvalAction: string, payload: any) {
        const result = await _callBusinessApi('POST', `/approvals/${approvalType}/${approvalAction}`, payload);
        return result;
    },
    async getMultiAllocationLinkedEntities(entityType: string, entityId: string) {
        const result = await _callBusinessApi('GET', `/multi-allocation/linked-entities/${entityType}/${entityId}`);
        return result;
    },
    async getMultiAllocationWorksheetExecution(worksheetId: string, period: string) {
        const result = await _callBusinessApi('GET', `/multi-allocation/worksheet/${worksheetId}/executions/${period}`);
        return result;
    },
    async getWorksheetExecutionGroup(worksheetId: string) {
        const result = await _callBusinessApi('GET', `/multi-allocation/worksheets/${worksheetId}/execution-group`);
        return result;
    },
    async saveWorksheetExecutionGroup(worksheetId: string, executionGroup: string) {
        const result = await _callBusinessApi('POST', `/multi-allocation/worksheets/${worksheetId}/execution-group`, {
            executionGroup: executionGroup
        });
        return result;
    },
    async getQuickSightEmbedUrl(topicId: string, userEmail: string) {
        const result = await _callReportingApi('POST', '/GetQuickSightEmbedUrl', { topicId, userEmail });
        return result.json();
    },
    async getAppConfig(featureKey: string, userEmail: string) {
        const result = await _callReportingApi('GET', `/app-config/${encodeURIComponent(featureKey)}/${encodeURIComponent(userEmail)}`);
        return result.json();
    },
    async editEntityDraft(componentType: string, entityId: string) {
        const result = await _callBusinessApi('POST', `/entity-draft/edit`, {componentType, entityId});
        return result;
    },
    async saveEntityDraftEdit(componentType: string, entityId: string) {
        const result = await _callBusinessApi('POST', `/entity-draft/save`, {componentType, entityId});
        return result;
    },
    async confirmEntityDraftEdit(componentType: string, entityId: string) {
        const result = await _callBusinessApi('POST', `/entity-draft/confirm`, {componentType, entityId});
        return result;
    },
    async revertEntityDraftEdit(componentType: string, entityId: string) {
        const result = await _callBusinessApi('POST', `/entity-draft/revert`, {componentType, entityId});
        return result;
    },
    // ----- Accounting calendar (MEC) -----
    async getCalendar(year?: string) {
        const result = await _callBusinessApi('GET', year ? `/calendar?year=${encodeURIComponent(year)}` : '/calendar');
        return result;
    },
    async createCustomTag(tag: CustomTag) {
        // The Smithy model marks `tag` as @httpPayload, so the request body IS the CustomTag
        // itself (not wrapped in { tag }).
        const result = await _callBusinessApi('POST', '/tags', tag);
        return result;
    },
    async updateCustomTag(tagId: string, tag: CustomTag, dateValue?: string) {
        // PUT /tags/{tagId}?dateValue=YYYY-MM-DD — dateValue locates the partition for
        // one-time tags; omitted for recurring tags (mirrors the delete contract).
        const params = new URLSearchParams();
        if (dateValue) {
            params.set('dateValue', dateValue);
        }
        const qs = params.toString();
        // `tag` is @httpPayload, so the body IS the CustomTag itself (not wrapped in { tag }).
        const result = await _callBusinessApi('PUT', `/tags/${encodeURIComponent(tagId)}${qs ? `?${qs}` : ''}`, tag);
        return result;
    },
    async deleteCustomTag(tagId: string, deleteScope: string, dateValue?: string, targetMonth?: string) {
        const params = new URLSearchParams({ deleteScope });
        if (dateValue) {
            params.set('dateValue', dateValue);
        }
        if (targetMonth) {
            params.set('targetMonth', targetMonth);
        }
        const result = await _callBusinessApi('DELETE', `/tags/${encodeURIComponent(tagId)}?${params.toString()}`, {});
        return result;
    },
    async validatePromotionDependencies(request: any) {
        return _callBusinessApi('POST', '/promotion/validate-dependencies', request);
    },
    async promoteComponent(request: any) {
        return _callBusinessApi('POST', '/promotion/promote', request);
    },
    async getPromotionStatus(params: { promotionId?: string; componentId?: string; componentType?: string }) {
        const query = new URLSearchParams();
        if (params.promotionId) query.set('promotionId', params.promotionId);
        if (params.componentId) query.set('componentId', params.componentId);
        if (params.componentType) query.set('componentType', params.componentType);
        return _callBusinessApi('GET', `/promotion/status?${query.toString()}`);
    },
    async getPromotionHistory(componentType: string, componentId: string, limit: number = 10) {
        return _callBusinessApi('GET', `/promotion/history?componentType=${componentType}&componentId=${componentId}&limit=${limit}`);
    },
    async getPromotionHistoryByPeriod(accountingPeriod: string, limit: number = 100, nextToken?: string) {
        const params = new URLSearchParams();
        params.set('accountingPeriod', accountingPeriod);
        params.set('limit', `${limit}`);
        if (nextToken) {
            params.set('nextToken', nextToken);
        }
        return _callBusinessApi('GET', `/promotion/history?${params.toString()}`);
    },
    async getPromotionWindowStatus() {
        return _callBusinessApi('GET', '/promotion/window-status');
    },
}

export default API;
